import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
} from "react";
import { initializeApp, getApps } from "firebase/app"; // Import getApps
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage"; // Import getStorage
import { getMessaging } from "firebase/messaging";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

// Check if Firebase is already initialized
let app;
if (getApps().length === 0) { // Use getApps() to check
  // console.log("Initializing Firebase...");
  app = initializeApp(firebaseConfig);
} else {
  // console.log("Firebase already initialized.");
  app = getApps()[0]; // Use the existing app
}

// Initialize Auth
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

const messaging = getMessaging(app);

// Initialize Firestore
let db;
try {
  db = getFirestore(app);
} catch (error) {
  console.error("Error initializing Firestore:", error);
}

// Authentication Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserToken, setCurrentUserToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // console.log("User:", user);
      setCurrentUser(user);

      if (user) {
        (async () => {
          const token = await user.getIdTokenResult();
          // console.log("User token:", token);
          setCurrentUserToken(token);
        })();
      } else {
        setCurrentUserToken(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refreshUser = async () => {
    const user = auth.currentUser;
    if (user) {
      await user.reload();
      // Force refresh
      const token = await user.getIdTokenResult(true);
      setCurrentUser(auth.currentUser);
      setCurrentUserToken(token);
    }
  };

  const value = useMemo(
    () => ({
      currentUser,
      currentUserToken,
      loading,
      refreshUser
    }),
    [currentUser, currentUserToken, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
};

export { db, app, auth, storage, messaging }; // Export storage
